import {
  SelectOptionOld,
  useWSSnackbar,
  WSActions,
  WSDivider,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import {
  QUICKBOOKS_ENTITY,
  RefreshModal,
  useQboRefreshModal
} from "../../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryEngagement } from "../../../../query/engagements/queries/useQueryEngagement";
import { useQueryEngagementsListAll } from "../../../../query/engagements/queries/useQueryEngagementsListAll";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksAccountExpenses,
  useQueryIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { useQueryPayee } from "../../../../query/payee/queries/useQueryPayee";
import { useMutationCreatePayeeEngagement } from "../../../../query/payeeEngagements/mutations/useMutationCreatePayeeEngagement";
import { useQueryPayeeEngagementsListByPayeeId } from "../../../../query/payeeEngagements/queries/useQueryPayeeEngagementsListByPayeeId";
import { FormPartialPayeeSearchField } from "../../../../shared/components/PayeeSearchField";
import { selectorIsQBOActive } from "../../../../shared/selectors/selectorIsQBOActive";
import { selectQuickbooksAccountOrItemOptions } from "../../../Integrations/selectors/selectQuickbooksAccountOrItemOptions";
import { selectQuickbooksUserOptions } from "../../../Integrations/selectors/selectQuickbooksUserOptions";
import { getPayeeNames } from "../../selectors/getPayeeNames";

export type CreateEngagementModalProps = {
  onClose(result: boolean): void;
  payeeId?: string;
  engagementId?: string;
};

export const CreateEngagementModal: React.FC<CreateEngagementModalProps> = ({
  payeeId,
  engagementId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const queryFeatureFlags = useFeatureFlags();
  const qboRefreshModal = useQboRefreshModal();

  const queryEngagementsTypes = useQueryEngagementsListAll(
    { payeeId },
    {
      enabled: !!payeeId
    }
  );

  const queryPayeeEngagements = useQueryPayeeEngagementsListByPayeeId(
    payeeId!,
    {},
    {
      enabled: !!payeeId
    }
  );

  const queryIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors =
    useQueryIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useQueryIntegrationsQuickbooksAccountExpenses();

  const queryPayee = useQueryPayee(payeeId!, {
    enabled: !!payeeId
  });

  const queryEngagement = useQueryEngagement(engagementId!, {
    enabled: !!engagementId
  });

  const isQuickbooksActive = selectorIsQBOActive(
    queryFeatureFlags?.data,
    queryIntegrationsQuickbooks.data
  );

  const [createEngagement, createEngagementMeta] =
    useMutationCreatePayeeEngagement();

  const quickbooksVendorOptions: SelectOptionOld[] =
    selectQuickbooksUserOptions(queryIntegrationsQuickbooksVendors.data);

  const quickbooksAccountsOptions: SelectOptionOld[] =
    selectQuickbooksAccountOrItemOptions(
      queryIntegrationsQuickbooksExpenseAccounts.data
    );

  const defaultAccount = (
    queryIntegrationsQuickbooksExpenseAccounts.data ?? []
  ).find(
    acct =>
      acct.accountId ===
      queryIntegrationsQuickbooks.data?.defaults?.expenseAccountId
  );

  const engagementsOptions =
    queryEngagementsTypes.data
      ?.filter(
        engagement =>
          !engagement.isDefault &&
          !queryPayeeEngagements.data?.some(
            eng => eng.engagementId === engagement.engagementId
          )
      )
      .map(engagement => ({
        label: engagement.name,
        value: engagement.engagementId
      })) || [];

  const payeeName = queryPayee.data
    ? getPayeeNames(queryPayee.data).getResolvedName()
    : "";

  return (
    <WSElement
      shimmer={
        payeeId
          ? queryPayee.isLoading ||
            queryEngagementsTypes.isLoading ||
            queryPayeeEngagements.isLoading
          : queryEngagement.isLoading
      }
    >
      <WSForm
        defaultValues={{
          qboVendorId: "",
          qboExpenseAccountId: "",
          engagementId: engagementId || "",
          payeeId: payeeId || ""
        }}
        validationSchema={Yup.object().shape({
          payeeId: Yup.string().required("Contractor is required!"),
          engagementId: Yup.string().required("Engagement is required")
        })}
        onSubmit={async data => {
          const result = await createEngagement({
            payeeId: data.payeeId,
            payerOwnedData: {
              integration: {
                quickbooks: {
                  vendorId: data.qboVendorId,
                  expenseAccountId: data.qboExpenseAccountId
                }
              }
            },
            engagementId: data.engagementId
          });

          if (result) {
            onClose(true);
            openSnackbar({
              message: "Assignment added successfully",
              type: "success"
            });
          }
        }}
      >
        <WSList gap="XL">
          {payeeId ? (
            <WSForm.Field
              name="payeeId"
              label="Contractor"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [{ value: payeeId || "", label: payeeName }]
              }}
            />
          ) : (
            <FormPartialPayeeSearchField
              name="payeeId"
              engagementId={engagementId!}
            />
          )}
          {engagementId ? (
            <WSForm.Field
              name="engagementId"
              label="Engagement"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [
                  {
                    value: engagementId,
                    label: queryEngagement.data?.name || "Current engagement"
                  }
                ]
              }}
            />
          ) : engagementsOptions.length > 0 ? (
            <WSForm.Field
              name="engagementId"
              label="Engagement"
              component={WSSelect}
              componentProps={{
                required: true,
                options: engagementsOptions
              }}
            />
          ) : (
            <WSText.ParagraphSm align="center">
              No engagements available
            </WSText.ParagraphSm>
          )}
          {isQuickbooksActive &&
            quickbooksVendorOptions.length > 0 &&
            quickbooksAccountsOptions.length > 0 && (
              <WSList gap="L">
                <WSDivider label="Quickbooks Mapping" />
                <WSInfoBox>
                  Because you have an active integration with QBO, please map
                  this new Contractor to an existing Vendor, or leave blank and
                  we will create a new one. You can also set a default expense
                  account for payable line items.
                </WSInfoBox>
                <WSForm.Field
                  name="qboVendorId"
                  component={WSSelect}
                  componentProps={{
                    placeholder: "Not mapped, create new Vendor",
                    options: quickbooksVendorOptions,
                    menuFooterAction: {
                      label: "Resync QBO Vendors",
                      icon: "refresh-v",
                      onClick: () => {
                        qboRefreshModal.open({
                          entity: QUICKBOOKS_ENTITY.VENDORS
                        });
                      }
                    }
                  }}
                  label="QBO Vendor"
                />
                <WSForm.Field
                  name="qboExpenseAccountId"
                  component={WSSelect}
                  componentProps={{
                    options: quickbooksAccountsOptions,
                    placeholder: `Use default "${defaultAccount?.fullyQualifiedName}"`,
                    menuFooterAction: {
                      label: "Resync QBO Expenses Accounts",
                      icon: "refresh-v",
                      onClick: () => {
                        qboRefreshModal.open({
                          entity: QUICKBOOKS_ENTITY.EXPENSES
                        });
                      }
                    }
                  }}
                  label="Default QBO Expense Account"
                />
                <RefreshModal />
              </WSList>
            )}

          <WSInfoBox title="About assignments">
            Assignments appear on the contractor's profile. They must complete
            any assignment-specific requirements to receive payments. You can
            archive or restore assignments at any time.
          </WSInfoBox>

          <WSErrorMessage
            contextKey="Engagement"
            error={createEngagementMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Add assignment",
                kind: "Primary",
                type: "submit",
                loading: createEngagementMeta.isLoading
              },
              {
                label: "Cancel",
                kind: "Secondary",
                type: "button",
                onClick() {
                  onClose(false);
                }
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSElement>
  );
};
